import React, { useState, useEffect, useMemo } from 'react';
import {
  Box,
  Button,
  Input,
  Textarea,
  Stack,
  Typography,
  CircularProgress,
  Alert,
  LinearProgress,
  AccordionSummary,
  AccordionDetails,
  AccordionGroup,
  FormControl,
  FormHelperText,
  FormLabel,
  Grid,
  Card,
  IconButton,
  Select,
  Option,
  Divider,
} from '@mui/joy';
import {
  CaretDown as ExpandMore,
  Plus as AddIcon,
  Trash as DeleteIcon,
  PencilSimple as EditIcon,
  CloudArrowUp as UploadIcon,
} from '@phosphor-icons/react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { TabbableAccordion } from '../../common/TabbableAccordion';
import api from '../../../lib/api';
import { setOrganizationTimezone } from '../../../lib/timezoneUtils';
import { GoogleFontsSelect } from '../../GoogleFontsSelect';
import { TopPageModal } from '../../TopPageModal';
import { ServiceAreaModal } from '../../ServiceAreaModal';
import { ColorPicker } from '../../cms/ColorPicker';
import { ManufacturerSelector } from '../../common/ManufacturerSelector';
import { AddressAutocompleteInput } from '../../common/AddressAutocompleteInput';
import { useOrganizationUsers } from '../../../hooks/useOrganizationUsers';
import { applyPrefill } from '../../../utils/prefill';
import type {
  Organization,
  TopPage,
  OrganizationOverview,
  AreaServed,
  ParsedAddress,
} from '../../../types';
import { useRegisterStepSave, type RegisterStepSave } from '../useRegisterStepSave';

interface KnowledgeGraphStepProps {
  registerSave: RegisterStepSave;
}

export const KnowledgeGraphStep: React.FC<KnowledgeGraphStepProps> = ({
  registerSave,
}) => {
  const queryClient = useQueryClient();
  const [formData, setFormData] = useState<Partial<Organization>>({});
  const [brandingData, setBrandingData] = useState<Partial<OrganizationOverview>>({});
  const [expandedSections, setExpandedSections] = useState<string[]>([
    'basic',
    'business',
  ]);
  const [topPageModalOpen, setTopPageModalOpen] = useState(false);
  const [editingTopPage, setEditingTopPage] = useState<TopPage | null>(null);
  const [serviceAreaModalOpen, setServiceAreaModalOpen] = useState(false);
  const [editingServiceArea, setEditingServiceArea] = useState<AreaServed | null>(null);
  const [uploadingLogoField, setUploadingLogoField] = useState<string | null>(null);
  const [logoUploadError, setLogoUploadError] = useState<string | null>(null);
  const [selectedManufacturers, setSelectedManufacturers] = useState<number[]>([]);
  const { users: organizationUsers } = useOrganizationUsers();

  // Fetch organization data
  const { data: orgData, isLoading: orgLoading } = useQuery({
    queryKey: ['organization-current'],
    queryFn: async () => {
      const response = await api.get<Organization>('/api/organizations/current/');
      return response.data;
    },
  });

  // Fetch organization overview (branding)
  const { data: overviewData, isLoading: overviewLoading } = useQuery({
    queryKey: ['organization-overview'],
    queryFn: async () => {
      const response = await api.get<OrganizationOverview>('/api/marketing/organization-overview/');
      return response.data;
    },
  });

  // Fetch top pages
  const { data: topPagesData, isLoading: topPagesLoading } = useQuery({
    queryKey: ['top-pages'],
    queryFn: async () => {
      const response = await api.get<any>('/api/top-pages/');
      const data = response.data;
      if (Array.isArray(data)) {
        return data as TopPage[];
      } else if (data?.results && Array.isArray(data.results)) {
        return data.results as TopPage[];
      }
      return [];
    },
  });

  // Fetch service areas
  const { data: serviceAreasData, isLoading: serviceAreasLoading } = useQuery({
    queryKey: ['areas-served'],
    queryFn: async () => {
      const response = await api.get<any>('/api/areas-served/');
      const data = response.data;
      if (Array.isArray(data)) {
        return data as AreaServed[];
      } else if (data?.results && Array.isArray(data.results)) {
        return data.results as AreaServed[];
      }
      return [];
    },
  });

  // Update mutations
  const updateOrg = useMutation({
    mutationFn: async (data: Partial<Organization>): Promise<Organization> => {
      const response = await api.patch<Organization>('/api/organizations/current/', data);
      return response.data;
    },
    onSuccess: (updatedData: Organization) => {
      queryClient.invalidateQueries({ queryKey: ['organization-current'] });
      if (updatedData.time_zone) {
        setOrganizationTimezone(updatedData.time_zone);
      }
    },
  });

  const updateBranding = useMutation({
    mutationFn: async (data: Partial<OrganizationOverview>) => {
      if (overviewData?.id) {
        const response = await api.patch(`/api/marketing/organization-overview/${overviewData.id}/`, data);
        return response.data;
      } else {
        const response = await api.post('/api/marketing/organization-overview/', data);
        return response.data;
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['organization-overview'] });
    },
  });

  // Top pages mutations
  const createTopPage = useMutation({
    mutationFn: async (data: Omit<TopPage, 'id'>) => {
      const response = await api.post('/api/top-pages/', data);
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['top-pages'] });
    },
  });

  const updateTopPage = useMutation({
    mutationFn: async ({ id, ...data }: TopPage) => {
      const response = await api.patch(`/api/top-pages/${id}/`, data);
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['top-pages'] });
    },
  });

  const deleteTopPage = useMutation({
    mutationFn: async (id: number) => {
      await api.delete(`/api/top-pages/${id}/`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['top-pages'] });
    },
  });

  // Service areas mutations
  const createServiceArea = useMutation({
    mutationFn: async (data: Omit<AreaServed, 'id'>) => {
      const response = await api.post('/api/areas-served/', data);
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['areas-served'] });
    },
  });

  const updateServiceArea = useMutation({
    mutationFn: async ({ id, ...data }: AreaServed) => {
      const response = await api.patch(`/api/areas-served/${id}/`, data);
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['areas-served'] });
    },
  });

  const deleteServiceArea = useMutation({
    mutationFn: async (id: number) => {
      await api.delete(`/api/areas-served/${id}/`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['areas-served'] });
    },
  });

  // Initialize form data
  useEffect(() => {
    if (orgData) {
      // The account holder's name is already on record from signup, so the
      // author opens seeded rather than blank. Only where nothing is saved,
      // and still editable — the content author is often someone else.
      setFormData(
        applyPrefill(orgData, {
          authorship_first_name: orgData.first_name,
          authorship_last_name: orgData.last_name,
        }),
      );
      if (orgData.time_zone) {
        setOrganizationTimezone(orgData.time_zone);
      }
      // Initialize selected manufacturers from org data
      if (orgData.manufacturers) {
        setSelectedManufacturers(orgData.manufacturers);
      }
    }
  }, [orgData]);

  useEffect(() => {
    if (overviewData) {
      setBrandingData(overviewData);
    }
  }, [overviewData]);

  // Calculate completion percentage
  const completion = useMemo(() => {
    const fields = [
      formData.name,
      formData.company_email || formData.email,
      formData.company_phone_number || formData.phone,
      formData.main_office_address_1,
      formData.main_office_city,
      formData.main_office_state,
      formData.main_office_zip,
      formData.website,
      formData.company_description,
      formData.number_of_employees,
      formData.latitude,
      formData.longitude,
      formData.time_zone,
      formData.google_embedded_map,
      formData.facebook_url,
      formData.instagram_url,
      formData.twitter_url,
      formData.linkedin_url,
      formData.youtube_url,
      formData.pinterest_url,
      formData.google_business_profile_url,
      formData.yelp_url,
      formData.bbb_url,
      formData.tiktok_url,
      formData.dnb_url,
      formData.indeed_url,
      formData.authorship_first_name,
      formData.authorship_last_name,
      formData.logo_wide_full_color || formData.logo_square_full_color,
      brandingData.overview,
      brandingData.brand_color_1,
      brandingData.brand_color_2,
      brandingData.brand_typeface_1,
      brandingData.brand_typeface_2,
      topPagesData && topPagesData.length > 0 ? 'has_pages' : null,
      serviceAreasData && serviceAreasData.length > 0 ? 'has_areas' : null,
    ];

    const filled = fields.filter(
      (f) => f !== null && f !== undefined && f !== ''
    ).length;
    return Math.round((filled / fields.length) * 100);
  }, [formData, brandingData, topPagesData, serviceAreasData]);

  const handleFieldChange = (field: keyof Organization, value: any) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  // Google's geometry hands back a raw double (-122.08424989999999), but the
  // organization stores coordinates as decimal(_,7) — DRF rejects anything
  // longer than seven decimal places outright.
  const toStoredCoordinate = (value: number | undefined): number | undefined =>
    value === undefined || !Number.isFinite(value) ? undefined : Number(value.toFixed(7));

  // Google Places already knows the rest of the address and its coordinates,
  // so picking a suggestion fills city/state/ZIP and the geocode instead of
  // asking for six values that all describe the same place.
  const handleAddressSelect = (parsed: ParsedAddress) => {
    setFormData((prev) => ({
      ...prev,
      main_office_address_1: parsed.address_1 || prev.main_office_address_1,
      main_office_city: parsed.city || prev.main_office_city,
      main_office_state: parsed.state || prev.main_office_state,
      main_office_zip: parsed.zip_code || prev.main_office_zip,
      latitude: toStoredCoordinate(parsed.lat) ?? prev.latitude,
      longitude: toStoredCoordinate(parsed.lng) ?? prev.longitude,
    }));
  };

  // The author is stored as a loose name pair, so the picker matches on it.
  // No match means the author is someone off the roster, which is allowed.
  // An empty pair matches nobody — several rosters carry a half-set-up user
  // whose own name fields are blank.
  const authorName = `${formData.authorship_first_name || ''}${formData.authorship_last_name || ''}`;
  const selectedAuthorId =
    (authorName &&
      organizationUsers.find(
        (user) =>
          user.first_name === formData.authorship_first_name &&
          user.last_name === formData.authorship_last_name,
      )?.id) ||
    null;

  const handleAuthorSelect = (userId: number | null) => {
    const user = organizationUsers.find((u) => u.id === userId);
    if (!user) return;
    setFormData((prev) => ({
      ...prev,
      authorship_first_name: user.first_name,
      authorship_last_name: user.last_name,
    }));
  };

  const handleBrandingChange = (field: keyof OrganizationOverview, value: any) => {
    if (field.startsWith('brand_color_') && value && value.startsWith('#')) {
      value = value.substring(1);
    }
    setBrandingData((prev) => ({ ...prev, [field]: value }));
  };

  const handleLogoUpload = async (file: File, fieldName: string) => {
    setUploadingLogoField(fieldName);
    setLogoUploadError(null);

    try {
      await api.uploadOrganizationLogo(file, fieldName);
      queryClient.invalidateQueries({ queryKey: ['organization-current'] });
    } catch (error: any) {
      const errorMessage = error?.responseData?.detail || error?.responseData?.[fieldName]?.[0] || 'Failed to upload logo';
      setLogoUploadError(errorMessage);
      setTimeout(() => setLogoUploadError(null), 5000);
    } finally {
      setUploadingLogoField(null);
    }
  };

  const handleTopPageSave = (data: Omit<TopPage, 'id'>) => {
    if (editingTopPage) {
      updateTopPage.mutate({ ...data, id: editingTopPage.id }, {
        onSuccess: () => {
          setTopPageModalOpen(false);
          setEditingTopPage(null);
        },
      });
    } else {
      createTopPage.mutate(data, {
        onSuccess: () => {
          setTopPageModalOpen(false);
        },
      });
    }
  };

  const handleServiceAreaSave = (data: Omit<AreaServed, 'id'>) => {
    if (editingServiceArea) {
      updateServiceArea.mutate({ ...data, id: editingServiceArea.id }, {
        onSuccess: () => {
          setServiceAreaModalOpen(false);
          setEditingServiceArea(null);
        },
      });
    } else {
      createServiceArea.mutate(data, {
        onSuccess: () => {
          setServiceAreaModalOpen(false);
        },
      });
    }
  };

  const handleSave = async () => {
    const {
      logo_square_full_color,
      logo_square_light,
      logo_square_dark,
      logo_wide_full_color,
      logo_wide_light,
      logo_wide_dark,
      ...orgDataWithoutLogos
    } = formData;

    // Include selected manufacturers in the update
    await updateOrg.mutateAsync({
      ...orgDataWithoutLogos,
      manufacturers: selectedManufacturers,
    });
    await updateBranding.mutateAsync(brandingData);
  };

  // The wizard's "Save & Continue" runs this before advancing. Errors are left
  // to propagate: swallowing them here is what let a failed save look like a
  // successful one and sent users to Settings to retype everything.
  useRegisterStepSave(registerSave, handleSave);

  const toggleSection = (section: string) => {
    setExpandedSections((prev) =>
      prev.includes(section)
        ? prev.filter((s) => s !== section)
        : [...prev, section]
    );
  };

  if (orgLoading || overviewLoading || topPagesLoading || serviceAreasLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      {(updateOrg.isError || updateBranding.isError) && (
        <Alert color="danger" sx={{ mb: 2 }}>
          Failed to save. Please try again.
        </Alert>
      )}

      {logoUploadError && (
        <Alert color="danger" sx={{ mb: 2 }}>
          {logoUploadError}
        </Alert>
      )}

      {/* Completion progress */}
      <Box sx={{ mb: 3 }}>
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            mb: 1,
          }}
        >
          <Typography level="title-md">Profile Completion</Typography>
          <Typography
            level="body-sm"
            sx={{
              color:
                completion >= 80
                  ? 'success.600'
                  : completion >= 50
                  ? 'warning.600'
                  : 'danger.600',
            }}
          >
            {completion}%
          </Typography>
        </Box>
        <LinearProgress
          determinate
          value={completion}
          color={
            completion >= 80
              ? 'success'
              : completion >= 50
              ? 'warning'
              : 'danger'
          }
          sx={{ '--LinearProgress-thickness': '8px' }}
        />
        <Typography level="body-xs" sx={{ mt: 1, color: 'neutral.600' }}>
          A complete profile helps with SEO and AI-generated content.
        </Typography>
      </Box>

      <AccordionGroup sx={{ gap: 2 }}>
        {/* Basic Info */}
        <TabbableAccordion
          expanded={expandedSections.includes('basic')}
          onExpandedChange={() => toggleSection('basic')}
        >
          <AccordionSummary indicator={<ExpandMore />}>
            <Typography level="title-sm">Basic Information</Typography>
          </AccordionSummary>
          <AccordionDetails>
            <Grid container spacing={2}>
              <Grid xs={12} sm={6}>
                <FormControl>
                  <FormLabel>Company Name</FormLabel>
                  <Input
                    value={formData.name || ''}
                    onChange={(e) => handleFieldChange('name', e.target.value)}
                  />
                </FormControl>
              </Grid>
              <Grid xs={12} sm={6}>
                <FormControl>
                  <FormLabel>Website</FormLabel>
                  <Input
                    type="url"
                    value={formData.website || ''}
                    onChange={(e) => handleFieldChange('website', e.target.value)}
                    placeholder="https://example.com"
                  />
                </FormControl>
              </Grid>
              <Grid xs={12} sm={6}>
                <FormControl>
                  <FormLabel>Phone</FormLabel>
                  <Input
                    value={formData.company_phone_number || ''}
                    onChange={(e) => handleFieldChange('company_phone_number', e.target.value)}
                  />
                </FormControl>
              </Grid>
              <Grid xs={12} sm={6}>
                <FormControl>
                  <FormLabel>Email</FormLabel>
                  <Input
                    type="email"
                    value={formData.company_email || ''}
                    onChange={(e) => handleFieldChange('company_email', e.target.value)}
                  />
                </FormControl>
              </Grid>
            </Grid>
          </AccordionDetails>
        </TabbableAccordion>

        {/* Address & Location */}
        <TabbableAccordion
          expanded={expandedSections.includes('address')}
          onExpandedChange={() => toggleSection('address')}
        >
          <AccordionSummary indicator={<ExpandMore />}>
            <Typography level="title-sm">Address & Location</Typography>
          </AccordionSummary>
          <AccordionDetails>
            <Grid container spacing={2}>
              <Grid xs={12}>
                <FormControl>
                  <FormLabel>Street Address</FormLabel>
                  <AddressAutocompleteInput
                    value={formData.main_office_address_1 || ''}
                    onChange={(value) => handleFieldChange('main_office_address_1', value)}
                    onAddressSelect={handleAddressSelect}
                    placeholder="Start typing your office address"
                  />
                  <FormHelperText>
                    Picking a suggestion fills in the city, state, ZIP and coordinates.
                  </FormHelperText>
                </FormControl>
              </Grid>
              <Grid xs={12}>
                <FormControl>
                  <FormLabel>Address Line 2</FormLabel>
                  <Input
                    value={formData.main_office_address_2 || ''}
                    onChange={(e) => handleFieldChange('main_office_address_2', e.target.value)}
                    placeholder="Suite, Unit, etc. (optional)"
                  />
                </FormControl>
              </Grid>
              <Grid xs={12} sm={5}>
                <FormControl>
                  <FormLabel>City</FormLabel>
                  <Input
                    value={formData.main_office_city || ''}
                    onChange={(e) => handleFieldChange('main_office_city', e.target.value)}
                  />
                </FormControl>
              </Grid>
              <Grid xs={6} sm={3}>
                <FormControl>
                  <FormLabel>State</FormLabel>
                  <Input
                    value={formData.main_office_state || ''}
                    onChange={(e) => handleFieldChange('main_office_state', e.target.value)}
                  />
                </FormControl>
              </Grid>
              <Grid xs={6} sm={4}>
                <FormControl>
                  <FormLabel>ZIP Code</FormLabel>
                  <Input
                    value={formData.main_office_zip || ''}
                    onChange={(e) => handleFieldChange('main_office_zip', e.target.value)}
                  />
                </FormControl>
              </Grid>
              <Grid xs={12} sm={6}>
                {/* Derived from the address above rather than typed: these
                    anchor the local-SEO ranking grid and the map. */}
                <FormControl>
                  <FormLabel>Coordinates</FormLabel>
                  <Input
                    readOnly
                    value={
                      formData.latitude != null && formData.longitude != null
                        ? `${formData.latitude}, ${formData.longitude}`
                        : ''
                    }
                    placeholder="Set when you pick an address above"
                  />
                </FormControl>
              </Grid>
              <Grid xs={12} sm={6}>
                <FormControl>
                  <FormLabel>Time Zone</FormLabel>
                  <Select
                    value={formData.time_zone || ''}
                    onChange={(_, value) => handleFieldChange('time_zone', value)}
                    placeholder="Select time zone..."
                  >
                    <Option value="America/New_York">Eastern Time (ET) - US</Option>
                    <Option value="America/Toronto">Eastern Time (ET) - Canada</Option>
                    <Option value="America/Halifax">Atlantic Time (AT) - Canada</Option>
                    <Option value="America/St_Johns">Newfoundland Time (NT) - Canada</Option>
                    <Option value="America/Chicago">Central Time (CT) - US</Option>
                    <Option value="America/Winnipeg">Central Time (CT) - Canada</Option>
                    <Option value="America/Mexico_City">Central Time (CT) - Mexico</Option>
                    <Option value="America/Denver">Mountain Time (MT) - US</Option>
                    <Option value="America/Edmonton">Mountain Time (MT) - Canada</Option>
                    <Option value="America/Hermosillo">Mountain Time (MT) - Mexico</Option>
                    <Option value="America/Los_Angeles">Pacific Time (PT) - US</Option>
                    <Option value="America/Vancouver">Pacific Time (PT) - Canada</Option>
                    <Option value="America/Tijuana">Pacific Time (PT) - Mexico</Option>
                    <Option value="America/Anchorage">Alaska Time (AKT) - US</Option>
                    <Option value="America/Adak">Hawaii-Aleutian Time (HAT) - US</Option>
                  </Select>
                </FormControl>
              </Grid>
              <Grid xs={12}>
                <FormControl>
                  <FormLabel>Google Maps Embed Code</FormLabel>
                  <Textarea
                    minRows={2}
                    value={formData.google_embedded_map || ''}
                    onChange={(e) => handleFieldChange('google_embedded_map', e.target.value)}
                    placeholder='<iframe src="https://www.google.com/maps/embed?..." ...'
                  />
                </FormControl>
              </Grid>
            </Grid>
          </AccordionDetails>
        </TabbableAccordion>

        {/* Business Details */}
        <TabbableAccordion
          expanded={expandedSections.includes('business')}
          onExpandedChange={() => toggleSection('business')}
        >
          <AccordionSummary indicator={<ExpandMore />}>
            <Typography level="title-sm">Business Details</Typography>
          </AccordionSummary>
          <AccordionDetails>
            <Grid container spacing={2}>
              <Grid xs={12}>
                <FormControl>
                  <FormLabel>Company Description</FormLabel>
                  <Textarea
                    value={formData.company_description || ''}
                    onChange={(e) => handleFieldChange('company_description', e.target.value)}
                    minRows={3}
                    placeholder="Tell customers about your company, services, and what makes you different..."
                  />
                </FormControl>
              </Grid>
              <Grid xs={12} sm={6}>
                <FormControl>
                  <FormLabel>Years in Business</FormLabel>
                  <Input
                    type="number"
                    value={formData.years_in_business || ''}
                    onChange={(e) => handleFieldChange('years_in_business', parseInt(e.target.value) || '')}
                  />
                </FormControl>
              </Grid>
              <Grid xs={12} sm={6}>
                <FormControl>
                  <FormLabel>Number of Employees</FormLabel>
                  <Input
                    type="number"
                    value={formData.number_of_employees || ''}
                    onChange={(e) => handleFieldChange('number_of_employees', parseInt(e.target.value) || '')}
                  />
                </FormControl>
              </Grid>
            </Grid>
          </AccordionDetails>
        </TabbableAccordion>

        {/* Authorship */}
        <TabbableAccordion
          expanded={expandedSections.includes('authorship')}
          onExpandedChange={() => toggleSection('authorship')}
        >
          <AccordionSummary indicator={<ExpandMore />}>
            <Typography level="title-sm">Authorship</Typography>
          </AccordionSummary>
          <AccordionDetails>
            <Grid container spacing={2}>
              <Grid xs={12}>
                <FormControl>
                  <FormLabel>Author</FormLabel>
                  <Select
                    value={selectedAuthorId}
                    onChange={(_, value) => handleAuthorSelect(value as number | null)}
                    placeholder="Choose a team member"
                  >
                    {organizationUsers.map((user) => (
                      <Option key={user.id} value={user.id}>
                        {user.full_name || `${user.first_name} ${user.last_name}`.trim() || user.email}
                      </Option>
                    ))}
                  </Select>
                  <FormHelperText>
                    Bylines your published content. Picking a team member fills the name below.
                  </FormHelperText>
                </FormControl>
              </Grid>
              <Grid xs={12} sm={6}>
                <FormControl>
                  <FormLabel>Author First Name</FormLabel>
                  <Input
                    value={formData.authorship_first_name || ''}
                    onChange={(e) => handleFieldChange('authorship_first_name', e.target.value)}
                    placeholder="Content author's first name"
                  />
                </FormControl>
              </Grid>
              <Grid xs={12} sm={6}>
                <FormControl>
                  <FormLabel>Author Last Name</FormLabel>
                  <Input
                    value={formData.authorship_last_name || ''}
                    onChange={(e) => handleFieldChange('authorship_last_name', e.target.value)}
                    placeholder="Content author's last name"
                  />
                </FormControl>
              </Grid>
            </Grid>
          </AccordionDetails>
        </TabbableAccordion>

        {/* Service Areas */}
        <TabbableAccordion
          expanded={expandedSections.includes('serviceAreas')}
          onExpandedChange={() => toggleSection('serviceAreas')}
        >
          <AccordionSummary indicator={<ExpandMore />}>
            <Typography level="title-sm">Service Areas</Typography>
          </AccordionSummary>
          <AccordionDetails>
            <Stack spacing={2}>
              <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
                <Button
                  variant="outlined"
                  color="primary"
                  size="sm"
                  startDecorator={<AddIcon />}
                  onClick={() => {
                    setEditingServiceArea(null);
                    setServiceAreaModalOpen(true);
                  }}
                >
                  Add Area
                </Button>
              </Box>

              {serviceAreasData && serviceAreasData.length > 0 ? (
                serviceAreasData.map((area) => (
                  <Box key={area.id} sx={{ p: 2, bgcolor: 'background.level1', borderRadius: 'md' }}>
                    <Grid container spacing={2} alignItems="center">
                      <Grid xs={12} sm={5}>
                        <Typography level="body-sm" fontWeight="md">{area.city}</Typography>
                      </Grid>
                      <Grid xs={12} sm={5}>
                        <Typography level="body-sm">{area.state}</Typography>
                      </Grid>
                      <Grid xs={12} sm={2} sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end' }}>
                        <IconButton
                          color="primary"
                          variant="plain"
                          size="sm"
                          onClick={() => {
                            setEditingServiceArea(area);
                            setServiceAreaModalOpen(true);
                          }}
                        >
                          <EditIcon />
                        </IconButton>
                        <IconButton
                          color="danger"
                          variant="plain"
                          size="sm"
                          onClick={() => {
                            if (window.confirm('Delete this service area?')) {
                              deleteServiceArea.mutate(area.id);
                            }
                          }}
                        >
                          <DeleteIcon />
                        </IconButton>
                      </Grid>
                    </Grid>
                  </Box>
                ))
              ) : (
                <Typography level="body-sm" sx={{ textAlign: 'center', color: 'neutral.500', py: 2 }}>
                  No service areas added yet.
                </Typography>
              )}
            </Stack>
          </AccordionDetails>
        </TabbableAccordion>

        {/* Manufacturers */}
        <TabbableAccordion
          expanded={expandedSections.includes('manufacturers')}
          onExpandedChange={() => toggleSection('manufacturers')}
        >
          <AccordionSummary indicator={<ExpandMore />}>
            <Typography level="title-sm">Manufacturers & Brands</Typography>
          </AccordionSummary>
          <AccordionDetails>
            <Typography level="body-sm" sx={{ mb: 2, color: 'neutral.600' }}>
              Select the manufacturers you work with. This helps with content generation and SEO.
            </Typography>
            <ManufacturerSelector
              selectedIds={selectedManufacturers}
              onChange={setSelectedManufacturers}
            />
          </AccordionDetails>
        </TabbableAccordion>

        {/* Social Profiles */}
        <TabbableAccordion
          expanded={expandedSections.includes('social')}
          onExpandedChange={() => toggleSection('social')}
        >
          <AccordionSummary indicator={<ExpandMore />}>
            <Typography level="title-sm">Social Profiles</Typography>
          </AccordionSummary>
          <AccordionDetails>
            <Grid container spacing={2}>
              <Grid xs={12} sm={6}>
                <FormControl>
                  <FormLabel>Facebook</FormLabel>
                  <Input
                    value={formData.facebook_url || ''}
                    onChange={(e) => handleFieldChange('facebook_url', e.target.value)}
                    placeholder="https://facebook.com/yourpage"
                  />
                </FormControl>
              </Grid>
              <Grid xs={12} sm={6}>
                <FormControl>
                  <FormLabel>Instagram</FormLabel>
                  <Input
                    value={formData.instagram_url || ''}
                    onChange={(e) => handleFieldChange('instagram_url', e.target.value)}
                    placeholder="https://instagram.com/youraccount"
                  />
                </FormControl>
              </Grid>
              <Grid xs={12} sm={6}>
                <FormControl>
                  <FormLabel>Twitter</FormLabel>
                  <Input
                    value={formData.twitter_url || ''}
                    onChange={(e) => handleFieldChange('twitter_url', e.target.value)}
                    placeholder="https://twitter.com/youraccount"
                  />
                </FormControl>
              </Grid>
              <Grid xs={12} sm={6}>
                <FormControl>
                  <FormLabel>LinkedIn</FormLabel>
                  <Input
                    value={formData.linkedin_url || ''}
                    onChange={(e) => handleFieldChange('linkedin_url', e.target.value)}
                    placeholder="https://linkedin.com/company/yourcompany"
                  />
                </FormControl>
              </Grid>
              <Grid xs={12} sm={6}>
                <FormControl>
                  <FormLabel>YouTube</FormLabel>
                  <Input
                    value={formData.youtube_url || ''}
                    onChange={(e) => handleFieldChange('youtube_url', e.target.value)}
                    placeholder="https://youtube.com/yourchannel"
                  />
                </FormControl>
              </Grid>
              <Grid xs={12} sm={6}>
                <FormControl>
                  <FormLabel>Pinterest</FormLabel>
                  <Input
                    value={formData.pinterest_url || ''}
                    onChange={(e) => handleFieldChange('pinterest_url', e.target.value)}
                    placeholder="https://pinterest.com/youraccount"
                  />
                </FormControl>
              </Grid>
              <Grid xs={12} sm={6}>
                <FormControl>
                  <FormLabel>Google Business Profile</FormLabel>
                  <Input
                    value={formData.google_business_profile_url || ''}
                    onChange={(e) => handleFieldChange('google_business_profile_url', e.target.value)}
                    placeholder="https://g.page/yourbusiness"
                  />
                </FormControl>
              </Grid>
              <Grid xs={12} sm={6}>
                <FormControl>
                  <FormLabel>Yelp</FormLabel>
                  <Input
                    value={formData.yelp_url || ''}
                    onChange={(e) => handleFieldChange('yelp_url', e.target.value)}
                    placeholder="https://yelp.com/biz/yourbusiness"
                  />
                </FormControl>
              </Grid>
              <Grid xs={12} sm={6}>
                <FormControl>
                  <FormLabel>Better Business Bureau (BBB)</FormLabel>
                  <Input
                    value={formData.bbb_url || ''}
                    onChange={(e) => handleFieldChange('bbb_url', e.target.value)}
                    placeholder="https://www.bbb.org/yourbusiness"
                  />
                </FormControl>
              </Grid>
              <Grid xs={12} sm={6}>
                <FormControl>
                  <FormLabel>TikTok</FormLabel>
                  <Input
                    value={formData.tiktok_url || ''}
                    onChange={(e) => handleFieldChange('tiktok_url', e.target.value)}
                    placeholder="https://tiktok.com/@youraccount"
                  />
                </FormControl>
              </Grid>
              <Grid xs={12} sm={6}>
                <FormControl>
                  <FormLabel>Dun & Bradstreet</FormLabel>
                  <Input
                    value={formData.dnb_url || ''}
                    onChange={(e) => handleFieldChange('dnb_url', e.target.value)}
                    placeholder="https://www.dnb.com/yourbusiness"
                  />
                </FormControl>
              </Grid>
              <Grid xs={12} sm={6}>
                <FormControl>
                  <FormLabel>Indeed</FormLabel>
                  <Input
                    value={formData.indeed_url || ''}
                    onChange={(e) => handleFieldChange('indeed_url', e.target.value)}
                    placeholder="https://indeed.com/cmp/yourcompany"
                  />
                </FormControl>
              </Grid>
            </Grid>
          </AccordionDetails>
        </TabbableAccordion>

        {/* Top Pages */}
        <TabbableAccordion
          expanded={expandedSections.includes('topPages')}
          onExpandedChange={() => toggleSection('topPages')}
        >
          <AccordionSummary indicator={<ExpandMore />}>
            <Typography level="title-sm">Top Pages</Typography>
          </AccordionSummary>
          <AccordionDetails>
            <Stack spacing={2}>
              <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
                <Button
                  variant="outlined"
                  color="primary"
                  size="sm"
                  startDecorator={<AddIcon />}
                  onClick={() => {
                    setEditingTopPage(null);
                    setTopPageModalOpen(true);
                  }}
                >
                  Add Page
                </Button>
              </Box>

              {topPagesData && topPagesData.length > 0 ? (
                topPagesData.map((page) => (
                  <Box key={page.id} sx={{ p: 2, bgcolor: 'background.level1', borderRadius: 'md' }}>
                    <Grid container spacing={2} alignItems="center">
                      <Grid xs={12} sm={4}>
                        <Typography level="body-sm" fontWeight="md" sx={{ wordBreak: 'break-all' }}>
                          {page.url}
                        </Typography>
                      </Grid>
                      <Grid xs={12} sm={6}>
                        <Typography level="body-xs" sx={{ color: 'neutral.600' }}>
                          {[page.keyword_1, page.keyword_2, page.keyword_3].filter(Boolean).join(', ')}
                        </Typography>
                      </Grid>
                      <Grid xs={12} sm={2} sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end' }}>
                        <IconButton
                          color="primary"
                          variant="plain"
                          size="sm"
                          onClick={() => {
                            setEditingTopPage(page);
                            setTopPageModalOpen(true);
                          }}
                        >
                          <EditIcon />
                        </IconButton>
                        <IconButton
                          color="danger"
                          variant="plain"
                          size="sm"
                          onClick={() => {
                            if (window.confirm('Delete this top page?')) {
                              deleteTopPage.mutate(page.id);
                            }
                          }}
                        >
                          <DeleteIcon />
                        </IconButton>
                      </Grid>
                    </Grid>
                  </Box>
                ))
              ) : (
                <Typography level="body-sm" sx={{ textAlign: 'center', color: 'neutral.500', py: 2 }}>
                  No top pages added yet.
                </Typography>
              )}
            </Stack>
          </AccordionDetails>
        </TabbableAccordion>

        {/* Branding */}
        <TabbableAccordion
          expanded={expandedSections.includes('branding')}
          onExpandedChange={() => toggleSection('branding')}
        >
          <AccordionSummary indicator={<ExpandMore />}>
            <Typography level="title-sm">Branding</Typography>
          </AccordionSummary>
          <AccordionDetails>
            {/* Square Logos */}
            <Typography level="title-sm" sx={{ mb: 1 }}>Square Logos</Typography>
            <Grid container spacing={2} sx={{ mb: 3 }}>
              <Grid xs={12} sm={4}>
                <Card variant="outlined" sx={{ p: 2 }}>
                  <Typography level="body-sm" sx={{ mb: 1 }}>Full-Color</Typography>
                  <Box sx={{ height: 60, bgcolor: 'background.level1', display: 'flex', alignItems: 'center', justifyContent: 'center', borderRadius: 'sm', mb: 1 }}>
                    {formData.logo_square_full_color ? (
                      <Box component="img" src={formData.logo_square_full_color} alt="Square Full-Color" sx={{ maxHeight: 50, maxWidth: '90%', objectFit: 'contain' }} />
                    ) : (
                      <Typography level="body-xs" sx={{ color: 'neutral.500' }}>No logo</Typography>
                    )}
                  </Box>
                  <Button variant="outlined" color="primary" size="sm" fullWidth startDecorator={uploadingLogoField === 'logo_square_full_color' ? <CircularProgress size="sm" /> : <UploadIcon />} component="label" disabled={uploadingLogoField !== null}>
                    {uploadingLogoField === 'logo_square_full_color' ? 'Uploading...' : 'Upload'}
                    <input type="file" hidden accept="image/*" disabled={uploadingLogoField !== null} onChange={(e) => { const file = e.target.files?.[0]; if (file) handleLogoUpload(file, 'logo_square_full_color'); }} />
                  </Button>
                </Card>
              </Grid>
              <Grid xs={12} sm={4}>
                <Card variant="outlined" sx={{ p: 2 }}>
                  <Typography level="body-sm" sx={{ mb: 1 }}>Light</Typography>
                  <Box sx={{ height: 60, bgcolor: 'neutral.800', display: 'flex', alignItems: 'center', justifyContent: 'center', borderRadius: 'sm', mb: 1 }}>
                    {formData.logo_square_light ? (
                      <Box component="img" src={formData.logo_square_light} alt="Square Light" sx={{ maxHeight: 50, maxWidth: '90%', objectFit: 'contain' }} />
                    ) : (
                      <Typography level="body-xs" sx={{ color: 'neutral.400' }}>No logo</Typography>
                    )}
                  </Box>
                  <Button variant="outlined" color="primary" size="sm" fullWidth startDecorator={uploadingLogoField === 'logo_square_light' ? <CircularProgress size="sm" /> : <UploadIcon />} component="label" disabled={uploadingLogoField !== null}>
                    {uploadingLogoField === 'logo_square_light' ? 'Uploading...' : 'Upload'}
                    <input type="file" hidden accept="image/*" disabled={uploadingLogoField !== null} onChange={(e) => { const file = e.target.files?.[0]; if (file) handleLogoUpload(file, 'logo_square_light'); }} />
                  </Button>
                </Card>
              </Grid>
              <Grid xs={12} sm={4}>
                <Card variant="outlined" sx={{ p: 2 }}>
                  <Typography level="body-sm" sx={{ mb: 1 }}>Dark</Typography>
                  <Box sx={{ height: 60, bgcolor: 'background.level1', display: 'flex', alignItems: 'center', justifyContent: 'center', borderRadius: 'sm', mb: 1 }}>
                    {formData.logo_square_dark ? (
                      <Box component="img" src={formData.logo_square_dark} alt="Square Dark" sx={{ maxHeight: 50, maxWidth: '90%', objectFit: 'contain' }} />
                    ) : (
                      <Typography level="body-xs" sx={{ color: 'neutral.500' }}>No logo</Typography>
                    )}
                  </Box>
                  <Button variant="outlined" color="primary" size="sm" fullWidth startDecorator={uploadingLogoField === 'logo_square_dark' ? <CircularProgress size="sm" /> : <UploadIcon />} component="label" disabled={uploadingLogoField !== null}>
                    {uploadingLogoField === 'logo_square_dark' ? 'Uploading...' : 'Upload'}
                    <input type="file" hidden accept="image/*" disabled={uploadingLogoField !== null} onChange={(e) => { const file = e.target.files?.[0]; if (file) handleLogoUpload(file, 'logo_square_dark'); }} />
                  </Button>
                </Card>
              </Grid>
            </Grid>

            {/* Wide Logos */}
            <Typography level="title-sm" sx={{ mb: 1 }}>Wide Logos</Typography>
            <Grid container spacing={2} sx={{ mb: 3 }}>
              <Grid xs={12} sm={4}>
                <Card variant="outlined" sx={{ p: 2 }}>
                  <Typography level="body-sm" sx={{ mb: 1 }}>Full-Color</Typography>
                  <Box sx={{ height: 50, bgcolor: 'background.level1', display: 'flex', alignItems: 'center', justifyContent: 'center', borderRadius: 'sm', mb: 1 }}>
                    {formData.logo_wide_full_color ? (
                      <Box component="img" src={formData.logo_wide_full_color} alt="Wide Full-Color" sx={{ maxHeight: 40, maxWidth: '90%', objectFit: 'contain' }} />
                    ) : (
                      <Typography level="body-xs" sx={{ color: 'neutral.500' }}>No logo</Typography>
                    )}
                  </Box>
                  <Button variant="outlined" color="primary" size="sm" fullWidth startDecorator={uploadingLogoField === 'logo_wide_full_color' ? <CircularProgress size="sm" /> : <UploadIcon />} component="label" disabled={uploadingLogoField !== null}>
                    {uploadingLogoField === 'logo_wide_full_color' ? 'Uploading...' : 'Upload'}
                    <input type="file" hidden accept="image/*" disabled={uploadingLogoField !== null} onChange={(e) => { const file = e.target.files?.[0]; if (file) handleLogoUpload(file, 'logo_wide_full_color'); }} />
                  </Button>
                </Card>
              </Grid>
              <Grid xs={12} sm={4}>
                <Card variant="outlined" sx={{ p: 2 }}>
                  <Typography level="body-sm" sx={{ mb: 1 }}>Light</Typography>
                  <Box sx={{ height: 50, bgcolor: 'neutral.800', display: 'flex', alignItems: 'center', justifyContent: 'center', borderRadius: 'sm', mb: 1 }}>
                    {formData.logo_wide_light ? (
                      <Box component="img" src={formData.logo_wide_light} alt="Wide Light" sx={{ maxHeight: 40, maxWidth: '90%', objectFit: 'contain' }} />
                    ) : (
                      <Typography level="body-xs" sx={{ color: 'neutral.400' }}>No logo</Typography>
                    )}
                  </Box>
                  <Button variant="outlined" color="primary" size="sm" fullWidth startDecorator={uploadingLogoField === 'logo_wide_light' ? <CircularProgress size="sm" /> : <UploadIcon />} component="label" disabled={uploadingLogoField !== null}>
                    {uploadingLogoField === 'logo_wide_light' ? 'Uploading...' : 'Upload'}
                    <input type="file" hidden accept="image/*" disabled={uploadingLogoField !== null} onChange={(e) => { const file = e.target.files?.[0]; if (file) handleLogoUpload(file, 'logo_wide_light'); }} />
                  </Button>
                </Card>
              </Grid>
              <Grid xs={12} sm={4}>
                <Card variant="outlined" sx={{ p: 2 }}>
                  <Typography level="body-sm" sx={{ mb: 1 }}>Dark</Typography>
                  <Box sx={{ height: 50, bgcolor: 'background.level1', display: 'flex', alignItems: 'center', justifyContent: 'center', borderRadius: 'sm', mb: 1 }}>
                    {formData.logo_wide_dark ? (
                      <Box component="img" src={formData.logo_wide_dark} alt="Wide Dark" sx={{ maxHeight: 40, maxWidth: '90%', objectFit: 'contain' }} />
                    ) : (
                      <Typography level="body-xs" sx={{ color: 'neutral.500' }}>No logo</Typography>
                    )}
                  </Box>
                  <Button variant="outlined" color="primary" size="sm" fullWidth startDecorator={uploadingLogoField === 'logo_wide_dark' ? <CircularProgress size="sm" /> : <UploadIcon />} component="label" disabled={uploadingLogoField !== null}>
                    {uploadingLogoField === 'logo_wide_dark' ? 'Uploading...' : 'Upload'}
                    <input type="file" hidden accept="image/*" disabled={uploadingLogoField !== null} onChange={(e) => { const file = e.target.files?.[0]; if (file) handleLogoUpload(file, 'logo_wide_dark'); }} />
                  </Button>
                </Card>
              </Grid>
            </Grid>

            <Divider sx={{ my: 2 }} />

            {/* Brand Overview */}
            <Grid container spacing={2}>
              <Grid xs={12}>
                <FormControl>
                  <FormLabel>Brand Overview</FormLabel>
                  <Textarea
                    minRows={2}
                    value={brandingData.overview || ''}
                    onChange={(e) => handleBrandingChange('overview', e.target.value)}
                    placeholder="Describe your brand identity, values, and messaging..."
                  />
                </FormControl>
              </Grid>

              {/* Brand Colors */}
              <Grid xs={12}>
                <Typography level="title-sm" sx={{ mt: 1, mb: 1 }}>Brand Colors</Typography>
              </Grid>
              <Grid xs={12} sm={4}>
                <ColorPicker
                  label="Primary Color"
                  value={brandingData.brand_color_1 ? `#${brandingData.brand_color_1}` : ''}
                  onChange={(color) => handleBrandingChange('brand_color_1', color)}
                  helpText="Your main brand color"
                />
              </Grid>
              <Grid xs={12} sm={4}>
                <ColorPicker
                  label="Secondary Color"
                  value={brandingData.brand_color_2 ? `#${brandingData.brand_color_2}` : ''}
                  onChange={(color) => handleBrandingChange('brand_color_2', color)}
                  helpText="Supporting brand color"
                />
              </Grid>
              <Grid xs={12} sm={4}>
                <ColorPicker
                  label="Accent Color 1"
                  value={brandingData.brand_color_3 ? `#${brandingData.brand_color_3}` : ''}
                  onChange={(color) => handleBrandingChange('brand_color_3', color)}
                  helpText="Additional accent color"
                />
              </Grid>
              <Grid xs={12} sm={4}>
                <ColorPicker
                  label="Accent Color 2"
                  value={brandingData.brand_color_4 ? `#${brandingData.brand_color_4}` : ''}
                  onChange={(color) => handleBrandingChange('brand_color_4', color)}
                  helpText="Optional accent color"
                />
              </Grid>
              <Grid xs={12} sm={4}>
                <ColorPicker
                  label="Accent Color 3"
                  value={brandingData.brand_color_5 ? `#${brandingData.brand_color_5}` : ''}
                  onChange={(color) => handleBrandingChange('brand_color_5', color)}
                  helpText="Optional accent color"
                />
              </Grid>

              {/* Typography */}
              <Grid xs={12}>
                <Typography level="title-sm" sx={{ mt: 2, mb: 1 }}>Typography</Typography>
              </Grid>
              <Grid xs={12} sm={6}>
                <GoogleFontsSelect
                  value={brandingData.brand_typeface_1 || ''}
                  onChange={(value) => handleBrandingChange('brand_typeface_1', value)}
                  label="Primary Typeface"
                  placeholder="Select primary font"
                />
              </Grid>
              <Grid xs={12} sm={6}>
                <GoogleFontsSelect
                  value={brandingData.brand_typeface_2 || ''}
                  onChange={(value) => handleBrandingChange('brand_typeface_2', value)}
                  label="Secondary Typeface"
                  placeholder="Select secondary font"
                />
              </Grid>
            </Grid>
          </AccordionDetails>
        </TabbableAccordion>
      </AccordionGroup>

      {/* Modals */}
      <TopPageModal
        open={topPageModalOpen}
        onClose={() => {
          setTopPageModalOpen(false);
          setEditingTopPage(null);
        }}
        topPage={editingTopPage}
        onSave={handleTopPageSave}
        loading={createTopPage.isPending || updateTopPage.isPending}
      />

      <ServiceAreaModal
        open={serviceAreaModalOpen}
        onClose={() => {
          setServiceAreaModalOpen(false);
          setEditingServiceArea(null);
        }}
        serviceArea={editingServiceArea}
        onSave={handleServiceAreaSave}
        loading={createServiceArea.isPending || updateServiceArea.isPending}
      />
    </Box>
  );
};

export default KnowledgeGraphStep;
